import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { inr } from "../lib/format";
import { Pill, PrimaryButton, ExplainButton, WhyBanner } from "../components/common";
import { MatchingChargeSheet } from "../components/monetization";
import { usePricing } from "../pricing";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { ChevronLeft, Loader2, PenLine, Landmark, Stamp, Check, ClipboardCheck } from "lucide-react";

export default function AgreementDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [signOpen, setSignOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);
  const { config: pconf } = usePricing();

  const load = useCallback(() => api.get(`/agreements/${id}`).then((r) => setA(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (a && a.status === "active" && a.owner_id === user?.id && !a.match_paid) setMatchOpen(a.id); }, [a, user?.id]);

  const sign = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/agreements/${id}/sign`, { otp }); setA(data); setSignOpen(false); setOtp(""); }
    catch (e) { alert(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  if (!a) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-terracotta" /></div>;
  const isOwner = a.owner_id === user?.id;
  const alreadySigned = isOwner ? a.owner_signed : a.tenant_signed;

  return (
    <div className="max-w-md md:max-w-4xl mx-auto min-h-screen bg-sand pb-28">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="agr-detail-back"><ChevronLeft className="w-6 h-6 text-slate-600" /></button>
        <div className="flex-1"><div className="font-head font-bold text-slate-900">{t("agreement")}</div><div className="text-xs text-slate-400">{a.property_address}</div></div>
        <Pill tone={a.status === "active" ? "trust" : "amber"}>{a.status}</Pill>
      </header>

      <div className="px-4 py-5 space-y-4">
        <div className="gs-card p-4 grid grid-cols-2 gap-3 text-sm">
          <div><div className="text-xs text-slate-400">{t("rent")}</div><div className="font-mono font-bold text-slate-900">{inr(a.rent)}</div></div>
          <div><div className="text-xs text-slate-400">{t("deposit")}</div><div className="font-mono font-bold text-slate-900">{inr(a.deposit)}</div></div>
        </div>

        {a.warnings?.length > 0 && <WhyBanner>{a.warnings.join(" ")}</WhyBanner>}

        <div>
          <div className="flex items-center gap-2 font-head font-semibold text-slate-800 mb-2"><ClipboardCheck className="w-5 h-5 text-terracotta" /> Clauses</div>
          <div className="space-y-2">
            {a.clauses.map((c, i) => (
              <div key={c.title} className="gs-card p-3.5" data-testid={`clause-${i}`}>
                <div className="font-semibold text-slate-800 text-sm">{c.title}</div>
                <div className="text-sm text-slate-500 mt-0.5">{c.text}</div>
                <div className="mt-2"><ExplainButton term={c.title} detail={c.text} label={t("explain_simply")} testId={`explain-clause-${i}`} /></div>
              </div>
            ))}
          </div>
        </div>

        {/* stamp duty */}
        <div className="gs-card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-semibold text-slate-800"><Stamp className="w-5 h-5 text-terracotta" /> {t("stamp_duty")}</div>
            <span className="font-mono font-bold text-slate-900">{inr(a.stamp_duty)}</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">{a.stamp_duty_percent}% of total rent · {a.law}</div>
          <div className="mt-2"><ExplainButton term="Stamp duty & e-stamping" detail="A government fee to make the agreement legally valid, paid online via e-stamp." testId="explain-stamp" /></div>
        </div>

        {/* sign status */}
        <div className="gs-card p-4 space-y-2">
          <div className="flex items-center justify-between text-sm"><span className="text-slate-600">Owner e-Sign</span>{a.owner_signed ? <Pill tone="trust"><Check className="w-3 h-3" /> Signed</Pill> : <Pill tone="slate">Pending</Pill>}</div>
          <div className="flex items-center justify-between text-sm"><span className="text-slate-600">Tenant e-Sign</span>{a.tenant_signed ? <Pill tone="trust"><Check className="w-3 h-3" /> Signed</Pill> : <Pill tone="slate">Pending</Pill>}</div>
        </div>

        {!alreadySigned && <PrimaryButton onClick={() => setSignOpen(true)} data-testid="esign-btn"><PenLine className="w-4 h-4 mr-2" /> {t("esign")}</PrimaryButton>}
        {a.status === "active" && a.rent_authority && <button data-testid="submit-authority-btn" onClick={() => alert("Submitted to district Rent Authority (demo).")} className="gs-tap w-full rounded-xl border border-slate-200 text-slate-700 font-semibold flex items-center justify-center gap-2"><Landmark className="w-4 h-4" /> {t("submit_authority")}</button>}
        {a.status === "active" && <button onClick={() => navigate(`/condition-report?agreement=${a.id}`)} data-testid="goto-condition-btn" className="gs-tap w-full rounded-xl bg-slate2 text-white font-semibold">{t("condition_report")}</button>}
      </div>

      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="max-w-[92vw] sm:max-w-md rounded-2xl">
          <DialogHeader><DialogTitle className="font-head text-left">{t("esign")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <WhyBanner>e-Signing means you legally agree to all clauses. Aadhaar OTP confirms it's really you.</WhyBanner>
            <p className="text-xs text-trust">Demo Aadhaar OTP: 123456</p>
            <Input className="rounded-xl gs-tap" data-testid="esign-otp" placeholder={t("enter_otp")} value={otp} onChange={(e) => setOtp(e.target.value)} />
            <PrimaryButton onClick={sign} disabled={busy || !otp} data-testid="esign-confirm-btn">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : t("esign")}</PrimaryButton>
          </div>
        </DialogContent>
      </Dialog>
      <MatchingChargeSheet open={matchOpen} onOpenChange={setMatchOpen} rent={a?.rent} config={pconf} onPaid={load} />
    </div>
  );
}
